import React, { useState, useEffect } from 'react';
import { analyticsService } from '../services/analytics';
import { useAuth } from '../contexts/AuthContext';
import type { AnalyticsData } from '../types';

interface ProgressDashboardProps {
  className?: string;
}

/**
 * ProgressDashboard - Analytics and progress tracking dashboard
 * Displays personal records, trends, and workout statistics
 * Requirements: 8.1, 8.2, 8.3, 8.4
 */
export const ProgressDashboard: React.FC<ProgressDashboardProps> = ({ className = '' }) => {
  const { user } = useAuth();
  const [overallStats, setOverallStats] = useState<any>(null);
  const [selectedExerciseAnalytics, setSelectedExerciseAnalytics] = useState<AnalyticsData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (user?.id) {
      loadOverallStats();
    }
  }, [user?.id]);

  const loadOverallStats = async () => {
    if (!user?.id) return;

    try {
      setIsLoading(true);
      setError(null);
      const stats = await analyticsService.getOverallStats(user.id);
      setOverallStats(stats);
    } catch (err: any) {
      console.error('Failed to load overall stats:', err);
      setError(err.message || 'Ошибка загрузки статистики');
    } finally {
      setIsLoading(false);
    }
  };

  const loadExerciseAnalytics = async (exerciseId: string) => {
    if (!user?.id) return;

    try {
      const analytics = await analyticsService.getExerciseAnalytics(exerciseId, user.id);
      setSelectedExerciseAnalytics(analytics);
    } catch (err: any) {
      console.error('Failed to load exercise analytics:', err);
      setError(err.message || 'Ошибка загрузки аналитики упражнения');
    }
  };

  if (isLoading) {
    return (
      <div className={`progress-dashboard ${className}`}>
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          <span className="ml-3 text-gray-600">Загрузка аналитики...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`progress-dashboard ${className}`}>
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <div className="text-red-500 text-xl mr-3">⚠️</div>
            <div>
              <h3 className="text-red-800 font-medium">Ошибка загрузки</h3>
              <p className="text-red-600 text-sm mt-1">{error}</p>
            </div>
          </div>
          <button
            onClick={loadOverallStats}
            className="mt-3 bg-red-600 text-white px-4 py-2 rounded-md text-sm hover:bg-red-700"
          >
            Повторить попытку
          </button>
        </div>
      </div>
    );
  }

  if (!overallStats) {
    return (
      <div className={`progress-dashboard ${className}`}>
        <div className="text-center py-12">
          <div className="text-4xl mb-4">📊</div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Нет данных для аналитики</h3>
          <p className="text-gray-600">Завершите несколько тренировок, чтобы увидеть статистику</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`progress-dashboard ${className} space-y-6`}>
      {/* Header */}
      <div className="text-center">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">📊 Аналитика прогресса</h1>
        <p className="text-gray-600">Ваши достижения и статистика тренировок</p>
      </div>

      {/* Overall Stats Cards */}
      <div className="grid grid-cols-2 gap-4">
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600">{overallStats.total_workouts}</div>
            <div className="text-sm text-gray-600">Всего тренировок</div>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-green-600">{overallStats.workouts_this_month}</div>
            <div className="text-sm text-gray-600">В этом месяце</div>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-purple-600">
              {Math.round(overallStats.total_volume / 1000)}к
            </div>
            <div className="text-sm text-gray-600">Общий объем (кг)</div>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-orange-600">{overallStats.average_workout_duration}</div>
            <div className="text-sm text-gray-600">Средняя длительность (мин)</div>
          </div>
        </div>
      </div>

      {/* Strength Progress */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h2 className="text-lg font-semibold mb-4 flex items-center">
          💪 Прогресс силы
        </h2>
        <div className="flex items-center justify-between">
          <div>
            <div className="text-3xl font-bold text-green-600">
              {overallStats.strength_progress > 0 ? '+' : ''}{overallStats.strength_progress}%
            </div>
            <div className="text-sm text-gray-600">За последние 3 месяца</div>
          </div>
          <div className="text-4xl">
            {overallStats.strength_progress > 0 ? '📈' : overallStats.strength_progress < 0 ? '📉' : '➡️'}
          </div>
        </div>
        {overallStats.strength_progress > 0 && (
          <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
            <p className="text-green-800 text-sm">
              Отличная работа! Ваши рабочие веса увеличились на {overallStats.strength_progress}%
            </p>
          </div>
        )}
      </div>

      {/* Favorite Exercises */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h2 className="text-lg font-semibold mb-4 flex items-center">
          ⭐ Любимые упражнения
        </h2>
        <div className="space-y-3">
          {overallStats.favorite_exercises.map((exercise: any, index: number) => (
            <div key={exercise.exercise_name} className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-sm font-medium mr-3">
                  {index + 1}
                </div>
                <span className="font-medium">{exercise.exercise_name}</span>
              </div>
              <div className="text-sm text-gray-600">
                {exercise.sessions} сессий
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Exercise Analytics Section */}
      {selectedExerciseAnalytics && (
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h2 className="text-lg font-semibold mb-4 flex items-center">
            🎯 Аналитика упражнения: {selectedExerciseAnalytics.exercise_name}
          </h2>
          
          {/* Personal Best */}
          <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <h3 className="font-medium text-yellow-800 mb-2">🏆 Личный рекорд</h3>
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold text-yellow-600">
                  {selectedExerciseAnalytics.personal_best.weight}кг × {selectedExerciseAnalytics.personal_best.reps}
                </div>
                <div className="text-sm text-yellow-700">
                  {new Date(selectedExerciseAnalytics.personal_best.date).toLocaleDateString('ru-RU')}
                </div>
              </div>
            </div>
          </div>

          {/* Progress Chart (Simplified) */}
          <div className="mb-4">
            <h3 className="font-medium mb-3">📈 Прогресс весов</h3>
            <div className="space-y-2">
              {selectedExerciseAnalytics.weight_progression.slice(-5).map((point, index) => (
                <div key={index} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-b-0">
                  <div className="text-sm text-gray-600">
                    {new Date(point.date).toLocaleDateString('ru-RU')}
                  </div>
                  <div className="flex items-center space-x-4">
                    <span className="text-sm font-medium">{point.max_weight}кг</span>
                    <span className="text-xs text-gray-500">{point.total_volume}кг объем</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Stats Summary */}
          <div className="grid grid-cols-2 gap-4">
            <div className="text-center p-3 bg-gray-50 rounded-lg">
              <div className="text-lg font-bold text-gray-900">{selectedExerciseAnalytics.total_sessions}</div>
              <div className="text-sm text-gray-600">Всего сессий</div>
            </div>
            <div className="text-center p-3 bg-gray-50 rounded-lg">
              <div className="text-lg font-bold text-gray-900">{selectedExerciseAnalytics.average_volume}</div>
              <div className="text-sm text-gray-600">Средний объем</div>
            </div>
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h2 className="text-lg font-semibold mb-4">🔍 Детальная аналитика</h2>
        <p className="text-gray-600 text-sm mb-4">
          Выберите упражнение из ваших любимых для детального анализа прогресса
        </p>
        <div className="space-y-2">
          {overallStats.favorite_exercises.slice(0, 3).map((exercise: any) => (
            <button
              key={exercise.exercise_name}
              onClick={() => loadExerciseAnalytics('mock-exercise-id')} // TODO: Get real exercise ID
              className="w-full text-left p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <div className="font-medium">{exercise.exercise_name}</div>
              <div className="text-sm text-gray-600">{exercise.sessions} сессий</div>
            </button>
          ))}
        </div>
      </div>

      {/* Motivational Footer */}
      <div className="bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-lg p-6 text-center">
        <div className="text-2xl mb-2">🎯</div>
        <h3 className="font-semibold text-gray-900 mb-2">Продолжайте тренироваться!</h3>
        <p className="text-gray-600 text-sm">
          Каждая тренировка приближает вас к вашим целям. Следите за прогрессом и не останавливайтесь!
        </p>
      </div>
    </div>
  );
};

export default ProgressDashboard;